/**
 * ПОЛНЫЙ ПУТЬ: D:\QR\QR-WEB\dashboard\src\components\QR\CreateQR.js
 * ВЕРСИЯ: 3.3.3 — FIX: remove duplicate code outside function
 * ДАТА: 20.11.2025
 */

import React, { useEffect, useMemo, useState } from 'react';
import './CreateQR.css';

const API_URL = 'https://qr-money.onrender.com';

/* ---------- Статусы ---------- */
const STATUS_LABELS = { active: 'Активен', claimed: 'Забран', expired: 'Истёк', cancelled: 'Отменён' };
const STATUS_CLASSES = {
  active: 'status-pill status-active',
  claimed: 'status-pill status-claimed',
  expired: 'status-pill status-expired',
  cancelled: 'status-pill status-cancelled',
};

/* ---------- Auth ---------- */
const getAuthToken = () =>
  localStorage.getItem('qr_money_jwt') ||
  localStorage.getItem('authToken') ||
  localStorage.getItem('token') ||
  localStorage.getItem('accessToken') ||
  '';

const getUserId = () => {
  const id = localStorage.getItem('userId');
  if (id) return id;
  const raw = localStorage.getItem('user') || localStorage.getItem('profile');
  if (!raw) return null;
  try { const u = JSON.parse(raw); return u?.id || u?.userId || null; } catch { return null; }
};

/* ---------- Utils ---------- */
const buildQrImageUrl = (qrCodeId, size = 160) => {
  if (!qrCodeId) return '';
  const data = encodeURIComponent(String(qrCodeId));
  return `https://api.qrserver.com/v1/create-qr-code/?size=${size}x${size}&data=${data}`;
};

/** Универсальный извлекатель массива из пагинированных и «сырых» ответов */
const extractItemsDeep = (json) => {
  const d = json?.data ?? json;
  const candidates = [
    d?.items, d?.rows, d,
    json?.items, json?.rows, json
  ];
  for (const c of candidates) if (Array.isArray(c)) return c;
  return [];
};

/** Приводим поля из БД к фронтовому виду */
const normalizeItem = (row) => ({
  id: row?.id ?? null,
  qrCodeId: row?.qr_code_id || row?.qrCodeId || '',
  amount: Number(row?.amount ?? 0),
  currency: row?.currency || 'DAR',
  status: String(row?.status || '').toLowerCase(),
  ttl: row?.ttl || row?.expiresAt || null,
});

function CreateQR() {
  // форма
  const [amount, setAmount] = useState('');
  const [ttlHours, setTtlHours] = useState('24');
  const [description, setDescription] = useState('');
  const [isCreating, setIsCreating] = useState(false);

  // список
  const [qrList, setQrList] = useState([]);
  const [isLoadingList, setIsLoadingList] = useState(false);
  const [cancelLoading, setCancelLoading] = useState({});

  // выбранный
  const [selected, setSelected] = useState(null);

  // фильтры
  const [showOnlyActive, setShowOnlyActive] = useState(false);
  const [searchId, setSearchId] = useState('');

  // сообщения
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  const userId = useMemo(getUserId, []);
  const token = useMemo(getAuthToken, []);

  const resetMessages = () => { setSuccess(''); setError(''); };

/* ------ загрузка списка ------ */
  const loadUserQRCodes = async () => {
    console.log('🚀 [CreateQR] loadUserQRCodes START');
    if (!userId) {
      console.warn('⚠️ [CreateQR] No userId, returning');
      return;
    }
    setIsLoadingList(true);
    resetMessages();
    try {
      const url = `${API_URL}/api/qr/user/${userId}/list?status=all&limit=50&offset=0`;
      console.log('📥 [CreateQR] Загружаю QR список с URL:', url);
      console.log('🔐 [CreateQR] Token exists:', !!token);
      console.log('👤 [CreateQR] UserId:', userId);
      
      const res = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      
      console.log('📡 [CreateQR] Response status:', res.status);
      console.log('📡 [CreateQR] Response ok:', res.ok);
      
      const json = await res.json().catch((err) => {
        console.error('❌ [CreateQR] JSON parse error:', err);
        return {};
      });
      
      console.log('🔍 [CreateQR] Raw JSON response:', JSON.stringify(json, null, 2));
      console.log('🔍 [CreateQR] json.success:', json?.success);
      console.log('🔍 [CreateQR] json.statusCode:', json?.statusCode);
      console.log('🔍 [CreateQR] json.data type:', typeof json?.data);
      console.log('🔍 [CreateQR] json.data keys:', Object.keys(json?.data || {}));
      console.log('🔍 [CreateQR] json.data.items:', json?.data?.items);
      console.log('🔍 [CreateQR] json.data.rows:', json?.data?.rows);
      console.log('🔍 [CreateQR] json.data itself:', json?.data);
      
      if (!res.ok) {
        console.error('❌ [CreateQR] Response not ok');
        throw new Error(json?.message || `Ошибка ${res.status}`);
      }

      console.log('🔧 [CreateQR] Accessing nested items structure...');
      const extracted = json?.data?.items?.items || [];
      console.log('✅ [CreateQR] extracted:', extracted);
      console.log('📊 [CreateQR] extracted.length:', extracted?.length);
      console.log('📊 [CreateQR] extracted is Array:', Array.isArray(extracted));
      
      if (!Array.isArray(extracted)) {
        console.error('❌ [CreateQR] extracted is NOT array!', typeof extracted);
      }
      
      const items = extracted.map((row) => {
        const normalized = normalizeItem(row);
        console.log('🔄 [CreateQR] Normalizing row:', row, '→', normalized);
        return normalized;
      });
      
      console.log('✅ [CreateQR] Final normalized items:', items);
      console.log('📊 [CreateQR] Total items count:', items.length);
      console.log('📊 [CreateQR] About to call setQrList with:', items);
      
      setQrList(items);
      console.log('✅ [CreateQR] setQrList called');

      if (items.length) {
        const firstActive = items.find((i) => i.status === 'active');
        console.log('🎯 [CreateQR] firstActive:', firstActive);
        const toSelect = firstActive || items[0];
        console.log('🎯 [CreateQR] toSelect:', toSelect);
        setSelected(toSelect);
        console.log('✅ [CreateQR] setSelected called with:', toSelect);
      } else {
        console.log('⚠️ [CreateQR] items.length = 0, setting selected to null');
        setSelected(null);
      }
      
      console.log('✅ [CreateQR] loadUserQRCodes COMPLETE');
    } catch (e) {
      console.error('❌ [CreateQR] loadUserQRCodes ERROR:', e);
      console.error('❌ [CreateQR] Error message:', e.message);
      console.error('❌ [CreateQR] Error stack:', e.stack);
      setError('Не удалось загрузить список QR. Попробуйте позже.');
      setQrList([]);
      setSelected(null);
    } finally {
      console.log('🏁 [CreateQR] loadUserQRCodes FINALLY - setIsLoadingList(false)');
      setIsLoadingList(false);
    }
  };

  useEffect(() => {
    if (userId) loadUserQRCodes();
    const onFocus = () => loadUserQRCodes();
    const onVis = () => document.visibilityState === 'visible' && loadUserQRCodes();
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVis);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [userId, token]);

  /* ------ создание ------ */
  const handleCreateQR = async (e) => {
    e.preventDefault();
    resetMessages();

    const amt = Number(amount);
    const ttl = Number(ttlHours);
    if (!amt || amt <= 0) return setError('Введите корректную сумму (минимум 1 DAR).');
    if (!ttl || ttl < 1) return setError('Введите срок действия от 1 часа.');
    if (!userId || !token) return setError('Вы не авторизованы. Перезайдите в систему.');

    setIsCreating(true);
    try {
      const res = await fetch(`${API_URL}/api/qr/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          amount: amt,
          ttl,
          currency: 'DAR',
          description: description || '',
        }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json?.message || 'Ошибка при создании QR.');

      const created = json?.data;
      const newEntry = created && (created.qrCodeId || created.qr_code_id)
        ? normalizeItem({
            id: created.id,
            qr_code_id: created.qrCodeId || created.qr_code_id,
            amount: created.amount,
            currency: created.currency || 'DAR',
            status: created.status || 'active',
            ttl: created.ttl,
          })
        : null;

      if (newEntry) {
        setQrList((prev) => [newEntry, ...(Array.isArray(prev) ? prev : [])]);
        setSelected(newEntry);
      }

      setSuccess('QR успешно создан и средства заблокированы.');
      setAmount('');
      setDescription('');
    } catch (e) {
      console.error('[CreateQR] handleCreateQR:', e);
      setError(e.message || 'Не удалось создать QR.');
    } finally {
      setIsCreating(false);
      setTimeout(() => loadUserQRCodes(), 500);
    }
  };

  /* ------ отмена ------ */
  const handleCancelQR = async (qrCodeId) => {
    if (!qrCodeId) return;
    if (!window.confirm('Отменить этот QR и вернуть средства?')) return;
    resetMessages();

    setCancelLoading((m) => ({ ...m, [qrCodeId]: true }));
    try {
      const res = await fetch(`${API_URL}/api/qr/cancel`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ qrCodeId }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json?.message || 'Не удалось отменить QR.');

      const newStatus = (json?.data?.status || 'cancelled').toLowerCase();
      setQrList((prev) =>
        (Array.isArray(prev) ? prev : []).map((r) =>
          r.qrCodeId === qrCodeId ? { ...r, status: newStatus } : r
        )
      );
      setSelected((cur) => (cur?.qrCodeId === qrCodeId ? { ...cur, status: newStatus } : cur));
      setSuccess('QR успешно отменён, средства разблокированы.');
    } catch (e) {
      console.error('[CreateQR] handleCancelQR:', e);
      setError(e.message || 'Ошибка при отмене QR.');
    } finally {
      setCancelLoading((m) => ({ ...m, [qrCodeId]: false }));
    }
  };

  /* ------ фильтрация/поиск ------ */
  const filtered = React.useMemo(() => {
    let rows = Array.isArray(qrList) ? qrList : [];
    if (showOnlyActive) rows = rows.filter((r) => r.status === 'active');
    if (searchId.trim()) {
      const q = searchId.trim().toLowerCase();
      rows = rows.filter((r) => (r.qrCodeId || '').toLowerCase().includes(q));
    }
    return rows;
  }, [qrList, showOnlyActive, searchId]);

  const renderStatus = (raw) => {
    const code = (raw || '').toLowerCase();
    return <span className={STATUS_CLASSES[code] || 'status-pill'}>{STATUS_LABELS[code] || raw || '—'}</span>;
  };

  /* ---------- RENDER ---------- */
  return (
    <div className="create-qr-page">
      <div className="create-qr-header">
        <h1>Создание QR</h1>
        <p className="create-qr-subtitle">
          Заблокируйте сумму на своём балансе и отправьте QR. Активные QR можно отменить и вернуть средства.
        </p>
      </div>

      {(success || error) && (
        <div className="create-qr-messages">
          {success && <div className="create-qr-message success">{success}</div>}
          {error && <div className="create-qr-message error">{error}</div>}
        </div>
      )}

      <div className="create-qr-main">
        {/* ЛЕВАЯ КОЛОННА: форма */}
        <div className="create-qr-card create-qr-form-card">
          <h2>Создать новый QR</h2>
          <form className="create-qr-form" onSubmit={handleCreateQR}>
            <div className="form-row">
              <label>Сумма (DAR)</label>
              <input
                type="number"
                placeholder="Введите сумму"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                disabled={isCreating}
              />
            </div>

            <div className="form-row">
              <label>Срок действия (часы)</label>
              <input
                type="number"
                placeholder="24"
                value={ttlHours}
                onChange={(e) => setTtlHours(e.target.value)}
                disabled={isCreating}
              />
              <p className="form-help">
                По умолчанию 24 часа. Можно отменить до истечения срока.
              </p>
            </div>

            <div className="form-row">
              <label>Описание (опционально)</label>
              <textarea
                placeholder="Например: Подарок, возврат долга..."
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                disabled={isCreating}
                rows={4}
              />
            </div>

            <button type="submit" className="create-qr-submit" disabled={isCreating || !userId || !token}>
              {isCreating ? 'Создание...' : 'Создать QR'}
            </button>
          </form>
        </div>

        {/* ПРАВАЯ КОЛОННА: превью + список */}
        <div>
          {/* Превью */}
          <div className="create-qr-card">
            <div className="my-qr-header">
              <h2>Мои QR-коды</h2>
              <button
                className="create-qr-refresh"
                onClick={loadUserQRCodes}
                disabled={isLoadingList}
              >
                {isLoadingList ? 'Загрузка...' : 'Обновить список'}
              </button>
            </div>

            {selected ? (
              <div className="qr-preview">
                <div className="qr-preview-media">
                  <img
                    src={buildQrImageUrl(selected.qrCodeId, 320)}
                    alt="QR"
                    className="qr-preview-image"
                  />
                </div>
                <div className="qr-preview-info">
                  <div className="qr-preview-line">
                    <span className="qr-preview-label">QR ID:</span>
                    <code className="qr-preview-id">{selected.qrCodeId}</code>
                    <button
                      className="btn-copy"
                      onClick={() => {
                        navigator.clipboard.writeText(selected.qrCodeId);
                        setSuccess('QR ID скопирован!');
                        setTimeout(() => setSuccess(''), 2000);
                      }}
                    >
                      Копировать
                    </button>
                  </div>

                  <div className="qr-preview-line">
                    <span className="qr-preview-label">Сумма:</span>
                    <strong>{selected.amount.toFixed(6)} DAR</strong>
                  </div>

                  <div className="qr-preview-line">
                    <span className="qr-preview-label">Статус:</span>
                    <div className="qr-preview-status">{renderStatus(selected.status)}</div>
                  </div>

                  <div className="qr-preview-actions">
                    <button
                      className="btn-outline"
                      onClick={() => {
                        const link = `${window.location.origin}/?qr=${selected.qrCodeId}`;
                        navigator.clipboard.writeText(link);
                        setSuccess('Ссылка на QR скопирована!');
                        setTimeout(() => setSuccess(''), 2000);
                      }}
                    >
                      Копировать ссылку
                    </button>

                    <a
                      href={buildQrImageUrl(selected.qrCodeId, 750)}
                      download={`qr-${selected.qrCodeId}.png`}
                      className="btn-primary"
                    >
                      Скачать PNG
                    </a>

                    {selected.status === 'active' && (
                      <button
                        className="btn-ghost"
                        onClick={() => handleCancelQR(selected.qrCodeId)}
                        disabled={cancelLoading[selected.qrCodeId]}
                      >
                        {cancelLoading[selected.qrCodeId] ? 'Отмена...' : 'Отменить'}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div className="qr-preview-empty">
                {isLoadingList ? 'Загрузка списка...' : 'Пока нет QR. Создайте первый слева.'}
              </div>
            )}
          </div>

          {/* Список */}
          <div className="create-qr-card">
            <div className="qr-filters-row">
              <div className="toggle-group">
                <button
                  className={`toggle-item ${!showOnlyActive ? 'active' : ''}`}
                  onClick={() => setShowOnlyActive(false)}
                >
                  Все
                </button>
                <button
                  className={`toggle-item ${showOnlyActive ? 'active' : ''}`}
                  onClick={() => setShowOnlyActive(true)}
                >
                  Только активные
                </button>
              </div>
              <input
                className="qr-search-input"
                placeholder="Поиск по QR ID..."
                value={searchId}
                onChange={(e) => setSearchId(e.target.value)}
              />
            </div>

            {filtered.length === 0 ? (
              <div className="qr-list-empty">
                {isLoadingList ? 'Загрузка...' : 'Ничего не найдено по текущим условиям.'}
              </div>
            ) : (
              <div className="qr-list-table-wrapper qr-list-table-wrapper--tight">
                <table className="qr-list-table">
                  <thead>
                    <tr>
                      <th>QR ID</th>
                      <th>QR-код</th>
                      <th>Сумма</th>
                      <th>Валюта</th>
                      <th>Статус</th>
                      <th>Истекает</th>
                      <th>Действие</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((row) => (
                      <tr
                        key={row.qrCodeId}
                        className={selected?.qrCodeId === row.qrCodeId ? 'qr-row-selected' : ''}
                        onClick={() => setSelected(row)}
                        style={{ cursor: 'pointer' }}
                      >
                        <td className="qr-list-id">{row.qrCodeId?.substring(0, 12)}...</td>
                        <td>
                          <img
                            src={buildQrImageUrl(row.qrCodeId, 64)}
                            alt="qr"
                            className="qr-image"
                          />
                        </td>
                        <td>{Number(row.amount || 0).toFixed(6)}</td>
                        <td>{row.currency || 'DAR'}</td>
                        <td>{renderStatus(row.status)}</td>
                        <td>{row.ttl ? new Date(row.ttl).toLocaleDateString('ru-RU') : '—'}</td>
                        <td>
                          {row.status === 'active' ? (
                            <button
                              className="qr-cancel-button"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleCancelQR(row.qrCodeId);
                              }}
                              disabled={cancelLoading[row.qrCodeId]}
                            >
                              {cancelLoading[row.qrCodeId] ? '...' : 'Отменить'}
                            </button>
                          ) : (
                            <span className="qr-list-action-placeholder">—</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* НИЗ СТРАНИЦЫ */}
      <div className="create-qr-info-card">
        <h2>Как это работает</h2>
        <ul className="how-it-works-list">
          <li>
            <span className="how-step-number">1</span>
            <span className="how-step-text">Создайте QR — сумма блокируется на вашем балансе.</span>
          </li>
          <li>
            <span className="how-step-number">2</span>
            <span className="how-step-text">Поделитесь QR с получателем (ссылка/изображение/скан).</span>
          </li>
          <li>
            <span className="how-step-number">3</span>
            <span className="how-step-text">Получатель сканирует и подтверждает получение.</span>
          </li>
          <li>
            <span className="how-step-number">4</span>
            <span className="how-step-text">Пока QR активен, вы можете отменить его и вернуть средства.</span>
          </li>
        </ul>
      </div>
    </div>
  );
}

export default CreateQR;
export { CreateQR };
